// ==============================================================================
// VERSION INFORMATION
// ==============================================================================
// __version__ = "1.2.0"
//
// Changelog:
//
// Version 1.2.0:
// - [Changed] Replaced the "Made by" branding in the footer with a formal
//   copyright notice for legal clarity.
// - [Removed] Removed the ClarityLB LLC logo as it is no longer needed.
//
// Version 1.1.1:
// - [Changed] Modified the SVG logo file to be colorable via CSS, ensuring it
//   displays correctly on both light and dark backgrounds.
//
// Version 1.1.0:
// - [Added] The footer now includes "Made by ClarityLB LLC" with the company
//   logo for consistent branding on public-facing pages.
//
// ==============================================================================
import React, { useState } from 'react';
import { Outlet, Link } from 'react-router-dom';
import Button from '../components/common/Button';
import logoSrc from '../assets/icons/Logo512.png';
import { APP_NAME } from '../config';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';


const PublicHeader: React.FC = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const handleLinkClick = () => {
        setIsMenuOpen(false);
    };

    return (
        <header className="bg-white shadow-md sticky top-0 z-50">
            <nav className="container mx-auto px-4 sm:px-6 py-3">
                <div className="flex justify-between items-center">
                    <Link to="/welcome" className="flex items-center gap-3" onClick={handleLinkClick}>
                        <img src={logoSrc} alt="LifeLine Logo" className="h-10 w-10" />
                        <span className="text-2xl font-bold text-primary">{APP_NAME}</span>
                    </Link>
                    
                    {/* Desktop Menu */}
                    <div className="hidden sm:flex items-center gap-2">
                        <Link to="/login">
                            <Button variant="outline">Login</Button>
                        </Link>
                        <Link to="/register/coach">
                            <Button variant="primary">Register as Coach</Button>
                        </Link>
                    </div>

                    {/* Mobile Menu Button */}
                    <div className="sm:hidden">
                        <Button onClick={() => setIsMenuOpen(!isMenuOpen)} variant="outline" className="p-2">
                            <span className="sr-only">Open main menu</span>
                            {isMenuOpen ? (
                                <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
                            ) : (
                                <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                            )}
                        </Button>
                    </div>
                </div>

                {/* Mobile Menu Dropdown */}
                {isMenuOpen && (
                    <div className="sm:hidden mt-3" id="mobile-menu">
                        <div className="px-2 pb-3 pt-2">
                            <Link to="/login" onClick={handleLinkClick} className="block mb-3">
                                <Button variant="outline" fullWidth>Login</Button>
                            </Link>
                            <Link to="/register/coach" onClick={handleLinkClick} className="block">
                                <Button variant="primary" fullWidth>Register as Coach</Button>
                            </Link>
                        </div>
                    </div>
                )}
            </nav>
        </header>
    );
};

const PublicFooter: React.FC = () => {
    return (
        <footer className="bg-neutral-dark text-white mt-auto">
            <div className="container mx-auto px-4 sm:px-6 py-4 text-center text-sm">
                <p>© 2025 ClarityLB LLC. All Rights Reserved.</p>
            </div>
        </footer>
    );
}

const PublicLayout: React.FC = () => {
    return (
        <div className="min-h-screen bg-neutral-light flex flex-col">
            <PublicHeader />
            <main className="flex-grow">
                <Outlet />
            </main>
            <PublicFooter />
        </div>
    );
};

export default PublicLayout;